import React, { useEffect, useState } from "react";
import Select from "react-select";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../api/axios";
import LoadingOverlay from "../../components/LoadingOverlay";

function InventoryExit() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [cart, setCart] = useState([]);
  const [observations, setObservations] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);

      const res = await api.get("/client-inventory-stock");

      setInventory(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Error cargando el inventario.");
    } finally {
      setLoading(false);
    }
  };

  const addProduct = () => {
    if (!selectedProduct) {
      toast.warning("Seleccione un insumo.");
      return;
    }

    const qty = Number(quantity);

    if (!qty || qty <= 0) {
      toast.warning("Ingrese una cantidad válida.");
      return;
    }

    const alreadyAdded = cart
      .filter((x) => x.product_id === selectedProduct.product_id)
      .reduce((sum, x) => sum + x.quantity, 0);

    const available = selectedProduct.quantity - alreadyAdded;

    if (available <= 0) {
      toast.warning("No hay más stock disponible.");
      return;
    }

    if (qty > available) {
      toast.warning(`Solo quedan ${available} unidades.`);
      return;
    }

    const index = cart.findIndex(
      (x) => x.product_id === selectedProduct.product_id,
    );

    if (index >= 0) {
      const updated = [...cart];
      updated[index].quantity += qty;
      setCart(updated);
    } else {
      setCart((prev) => [
        ...prev,
        {
          product_id: selectedProduct.product_id,
          name: selectedProduct.product.name,
          stock: selectedProduct.quantity,
          quantity: qty,
        },
      ]);
    }

    setSelectedProduct(null);
    setQuantity("");
  };

  const removeProduct = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const decreaseQuantity = (index) => {
    const updated = [...cart];

    if (updated[index].quantity <= 1) {
      removeProduct(index);
      return;
    }

    updated[index].quantity--;

    setCart(updated);
  };

  const increaseQuantity = (index) => {
    const updated = [...cart];

    if (updated[index].quantity >= updated[index].stock) {
      toast.warning(`Solo hay ${updated[index].stock} unidades disponibles.`);
      return;
    }

    updated[index].quantity++;

    setCart(updated);
  };

  const saveExit = async () => {
    try {
      setShowConfirmModal(false);

      const data = {
        observations,
        products: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
      };

      await api.post("/client-inventory-stock/exit", data);

      toast.success("Salida registrada correctamente.");

      navigate("/my-inventory");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.message || "Error registrando la salida.",
      );
    }
  };

  return (
    <div className="mx-auto max-w-7xl rounded-xl bg-white p-4 shadow-md sm:p-6">
      <LoadingOverlay loading={loading} />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-800 sm:text-2xl">
          Registrar salida de bodega
        </h2>

        <button
          onClick={() => navigate("/my-inventory")}
          className="flex items-center gap-2 self-start text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft size={18} />
          Volver
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Insumo</label>

          <Select
            value={
              selectedProduct
                ? {
                    value: selectedProduct.product_id,
                    label: `${selectedProduct.product.name}`,
                  }
                : null
            }
            options={inventory.map((item) => ({
              value: item.product_id,
              label: `${item.product.name}`,
              data: item,
            }))}
            onChange={(option) => setSelectedProduct(option.data)}
            placeholder="Seleccione un insumo..."
            isClearable
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Stock actual</label>

          <input
            readOnly
            value={selectedProduct?.quantity || ""}
            className="w-full border rounded px-3 py-2 bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Cantidad a entregar
          </label>

          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full border px-4 py-2 rounded-lg"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={addProduct}
          disabled={!selectedProduct || !quantity}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-white shadow hover:bg-blue-700 disabled:bg-gray-400 sm:w-auto"
        >
          <Plus size={18} />
          Agregar Producto
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl shadow mt-6">
        <table className="min-w-[640px] bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 uppercase text-sm text-center">
              <th className="px-3 py-3 sm:px-6">Producto</th>
              <th className="px-3 py-3 sm:px-6">Stock</th>
              <th className="px-3 py-3 sm:px-6">Cantidad</th>
              <th className="w-28 px-3 py-3 sm:px-6">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {cart.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 italic text-gray-400 sm:px-6">
                  No hay productos agregados.
                </td>
              </tr>
            ) : (
              cart.map((item, index) => (
                <tr
                  key={index}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-3 py-4 font-medium sm:px-6">{item.name}</td>

                  <td className="px-3 py-4 font-semibold text-gray-700 sm:px-6">
                    {item.stock}
                  </td>

                  <td className="px-3 py-4 sm:px-6">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => decreaseQuantity(index)}
                        className="w-8 h-8 rounded bg-red-500 hover:bg-red-600 text-white font-bold"
                      >
                        -
                      </button>

                      <span className="w-8 text-center font-bold text-blue-600">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => increaseQuantity(index)}
                        className="w-8 h-8 rounded bg-green-500 hover:bg-green-600 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </td>

                  <td className="px-3 py-4 sm:px-6">
                    <button
                      onClick={() => removeProduct(index)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-600 hover:text-white transition"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <label className="font-semibold">Observaciones</label>

        <textarea
          rows={3}
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
          placeholder="Observaciones de la salida (Opcional)"
          className="w-full mt-1 border rounded px-3 py-2"
        />
      </div>

      <div className="flex justify-end mt-6">
        <button
          disabled={cart.length === 0}
          onClick={() => setShowConfirmModal(true)}
          className={`px-6 py-2 rounded-lg flex items-center gap-2 text-white ${
            cart.length === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          <Save size={18} />
          Registrar salida
        </button>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
            <h3 className="text-xl font-bold text-center">Confirmar salida</h3>

            <p className="text-gray-500 text-center mt-3">
              Se registrará la salida de
              <strong> {cart.length} producto(s)</strong> del inventario del
              área.
            </p>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                Cancelar
              </button>

              <button
                onClick={saveExit}
                className="flex-1 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryExit;
